import type React from 'react'
import type { PageTab } from '../types/internal-types'
import MobileTabMenu, { type TabItem } from './MobileTabMenu'
import TabMenu from './TabMenu'

interface PageWrapperProps {
  children: React.ReactNode
  title?: string
  tabs?: PageTab[]
  fixedWidth?: boolean
  customMaxWidth?: string
  description?: string
  mobileTabs?: TabItem[]
  goBack?: () => void
  isLoading?: boolean
  actions?: React.ReactNode
  // When the page is rendered inside a host that already owns the screen
  // height and scroll (e.g. VeliOnboardingShell, which stacks a banner above
  // it), the wrapper must not claim its own 100vh + scroll — doing so pushes
  // its lower half below the fold and makes later content unreachable. In that
  // case it renders at natural height and lets the host scroll.
  embedded?: boolean
}

const PageWrapper: React.FC<PageWrapperProps> = ({
  children,
  title,
  tabs,
  fixedWidth,
  customMaxWidth,
  description,
  mobileTabs,
  goBack,
  isLoading,
  actions,
  embedded,
}) => {
  return (
    // This is not a sustainable solution with pb
    //
    // h-full (not h-screen): the app shell (AppNavigator) is a flex column whose
    // banner area is fixed and whose content area is flex-1 — so a page must fill
    // the *remaining* height, not a full 100vh, otherwise a visible banner pushes
    // the page's lower half below the fold and its bottom can't be scrolled to.
    <div
      className={`${
        embedded ? '' : 'h-full overflow-y-auto'
      } pb-40 lg:pb-20 flex flex-col`}
    >
      <div className="sticky left-0 top-0 right-0 z-50">
        {title && (
          <TabMenu
            title={title}
            description={description}
            tabs={tabs}
            goBack={goBack}
            isLoading={isLoading}
            actions={actions}
          />
        )}
        {mobileTabs && <MobileTabMenu tabs={mobileTabs} />}
      </div>

      <div className="flex flex-col items-center h-full">
        <div
          style={{ maxWidth: customMaxWidth }}
          className={`w-full p-3 h-full flex flex-col ${
            fixedWidth ? 'max-w-3xl mt-5' : 'p-5'
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

export default PageWrapper
